/**
 * The origin+source-pinned postMessage protocol between a host page and the
 * `bln-support-bot` widget iframe (Story 4.4). This is the SINGLE trust boundary
 * on the client: the widget is public and unauthenticated, so both sides only
 * ever act on a message that passes the fail-closed `parseWidgetMessage` check.
 *
 * Adapted from `@bluedrop-learning-networks/skillrx-embed`'s `messages.ts`, but
 * with our own `bln-support-bot`-namespaced envelope and a stricter validator
 * that pins BOTH the peer `origin` and the peer `Window` (`event.source`), never
 * a substring/`startsWith` match and never `'*'`.
 *
 * `WidgetMessage` is a widget-side TRANSPORT type (like `ChatStreamEvent`), not a
 * shared DATA type, so it lives in the embed and is never imported from
 * `@bln/shared/types` (coding-standards.md#Type sharing).
 */

/** The envelope discriminant every message on this channel MUST carry. */
export const WIDGET_MESSAGE_SOURCE = 'bln-support-bot' as const;
/** The protocol version. Bump only on a breaking envelope change. */
export const WIDGET_MESSAGE_VERSION = 1 as const;

/** Widget → host: "I'm mounted, send AUTH." Carries no payload. */
export interface ReadyMessage {
  source: typeof WIDGET_MESSAGE_SOURCE;
  v: typeof WIDGET_MESSAGE_VERSION;
  type: 'READY';
}

/**
 * Host → widget: the session bootstrap. This is how the host passes the resolved
 * `tenant` plus the anonymized user id + locale (AC 2). `anonUserId` is a hashed,
 * analytics-only value — NEVER a credential; the widget holds no tokens.
 */
export interface AuthMessage {
  source: typeof WIDGET_MESSAGE_SOURCE;
  v: typeof WIDGET_MESSAGE_VERSION;
  type: 'AUTH';
  payload: {
    tenant: string;
    locale?: string;
    anonUserId?: string;
    brandName?: string;
    /**
     * Host-navigation mode. When true (the support wrapper sets it), the widget
     * intercepts in-answer link clicks and posts `OPEN_LINK` to the host instead
     * of opening a new tab, so the host can navigate its background surface (the
     * wrapped KB iframe). Absent/false ⇒ links open normally (new tab).
     */
    hostNav?: boolean;
  };
}

/** Widget → host: report the desired content height (mobile full-screen sizing). */
export interface ResizeMessage {
  source: typeof WIDGET_MESSAGE_SOURCE;
  v: typeof WIDGET_MESSAGE_VERSION;
  type: 'RESIZE';
  payload: { height: number };
}

/** The only video provider the host-page lightbox will embed (Interaction 2). */
export type OpenVideoProvider = 'vimeo';

/**
 * Widget → host (launcher): open a cited video full-screen on the HOST page,
 * OUTSIDE the widget iframe (an in-iframe modal cannot cover the host). Sent when
 * a `VideoCard` thumbnail is activated for a lightbox-capable provider. The
 * launcher validates origin + `source` + this shape, then embeds ONLY
 * `player.vimeo.com` for the pinned `videoId` — it never embeds `url` directly
 * (`url` is the article link, informational). `startMs` is the cited transcript
 * offset for a deep link.
 */
export interface OpenVideoMessage {
  source: typeof WIDGET_MESSAGE_SOURCE;
  v: typeof WIDGET_MESSAGE_VERSION;
  type: 'OPEN_VIDEO';
  payload: {
    provider: OpenVideoProvider;
    videoId: string;
    url: string;
    title: string;
    startMs?: number;
  };
}

/**
 * Widget → host: navigate the host's BACKGROUND surface to `url` instead of the
 * widget opening a new tab. Sent (only in `hostNav` mode) when an in-answer link
 * — a citation pill, a video card's article link, or the IDK ticket CTA — is
 * clicked. The support wrapper handles it by pointing its KB `<iframe>` at `url`,
 * so the reference opens in place behind the chat. `url` is validated to be an
 * absolute http(s) URL (never `javascript:`), fail-closed.
 */
export interface OpenLinkMessage {
  source: typeof WIDGET_MESSAGE_SOURCE;
  v: typeof WIDGET_MESSAGE_VERSION;
  type: 'OPEN_LINK';
  payload: { url: string };
}

/**
 * The single versioned envelope shared by both sides. The concrete union is
 * more precise than the `{ payload?: unknown }` shape the story describes, but
 * satisfies it — `parseWidgetMessage` narrows an untrusted body to exactly one
 * of these variants (or `null`).
 */
export type WidgetMessage =
  | ReadyMessage
  | AuthMessage
  | ResizeMessage
  | OpenVideoMessage
  | OpenLinkMessage;

/** What `parseWidgetMessage` pins an incoming event against. */
export interface MessagePin {
  /** The exact peer origin (host pins the widget origin; widget pins the host). */
  origin: string;
  /** The exact peer `Window` (host: `iframe.contentWindow`; widget: `window.parent`). */
  source: Window | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Validate the `AUTH` payload shape (tenant required; locale/anonUserId optional strings). */
function parseAuthPayload(payload: unknown): AuthMessage['payload'] | null {
  if (!isRecord(payload)) {
    return null;
  }
  const { tenant, locale, anonUserId, brandName, hostNav } = payload;
  if (typeof tenant !== 'string' || tenant.length === 0) {
    return null;
  }
  if (locale !== undefined && typeof locale !== 'string') {
    return null;
  }
  if (anonUserId !== undefined && typeof anonUserId !== 'string') {
    return null;
  }
  if (brandName !== undefined && typeof brandName !== 'string') {
    return null;
  }
  if (hostNav !== undefined && typeof hostNav !== 'boolean') {
    return null;
  }
  return {
    tenant,
    ...(locale !== undefined ? { locale } : {}),
    ...(anonUserId !== undefined ? { anonUserId } : {}),
    ...(brandName !== undefined ? { brandName } : {}),
    ...(hostNav !== undefined ? { hostNav } : {}),
  };
}

/** Validate the `OPEN_LINK` payload: an absolute http(s) URL. Anything else → null. */
function parseOpenLinkPayload(payload: unknown): OpenLinkMessage['payload'] | null {
  if (!isRecord(payload)) {
    return null;
  }
  const { url } = payload;
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
    return null;
  }
  return { url };
}

/**
 * Validate the `OPEN_VIDEO` payload: a KNOWN provider, a non-empty `videoId`, a
 * `url` + `title` string, and an optional finite non-negative `startMs`. Anything
 * else → `null` (fail-closed), so the launcher never embeds an unpinned/unknown
 * video.
 */
function parseOpenVideoPayload(payload: unknown): OpenVideoMessage['payload'] | null {
  if (!isRecord(payload)) {
    return null;
  }
  const { provider, videoId, url, title, startMs } = payload;
  if (provider !== 'vimeo') {
    return null;
  }
  if (typeof videoId !== 'string' || videoId.length === 0) {
    return null;
  }
  if (typeof url !== 'string' || typeof title !== 'string') {
    return null;
  }
  if (startMs !== undefined && (typeof startMs !== 'number' || !Number.isFinite(startMs))) {
    return null;
  }
  return {
    provider,
    videoId,
    url,
    title,
    ...(startMs !== undefined ? { startMs } : {}),
  };
}

/** Validate the `RESIZE` payload shape (finite numeric height). */
function parseResizePayload(payload: unknown): ResizeMessage['payload'] | null {
  if (!isRecord(payload)) {
    return null;
  }
  const { height } = payload;
  if (typeof height !== 'number' || !Number.isFinite(height)) {
    return null;
  }
  return { height };
}

/**
 * The fail-closed validator — the load-bearing security guarantee (AC 2). Returns
 * `null` (NEVER throws) unless ALL of these hold:
 *
 *  (a) `event.origin === expect.origin` — an exact string match, never a
 *      substring/`startsWith`, never `'*'`;
 *  (b) `event.source === expect.source` — identity-pinned to the specific
 *      `Window` (and never to a `null` expectation);
 *  (c) the body carries `source === 'bln-support-bot'`, `v === 1`, a KNOWN
 *      `type`, and a payload matching that type's shape.
 *
 * Anything malformed / spoofed / unknown is dropped silently. Loosening any one
 * of these checks must turn the corresponding red-green assertion in
 * `messages.test.ts` red.
 */
export function parseWidgetMessage(event: MessageEvent, expect: MessagePin): WidgetMessage | null {
  // (a) Origin-pinned — exact match only. An empty expected origin trusts nothing.
  if (expect.origin.length === 0 || event.origin !== expect.origin) {
    return null;
  }
  // (b) Source-pinned — identity match to the expected Window. A null expectation
  // (iframe not yet mounted / no parent) can never match, so it fails closed.
  if (expect.source === null || event.source !== expect.source) {
    return null;
  }
  // (c) Schema-validate the untrusted body.
  const data = event.data;
  if (!isRecord(data)) {
    return null;
  }
  if (data.source !== WIDGET_MESSAGE_SOURCE || data.v !== WIDGET_MESSAGE_VERSION) {
    return null;
  }
  switch (data.type) {
    case 'READY':
      return { source: WIDGET_MESSAGE_SOURCE, v: WIDGET_MESSAGE_VERSION, type: 'READY' };
    case 'AUTH': {
      const payload = parseAuthPayload(data.payload);
      if (payload === null) {
        return null;
      }
      return { source: WIDGET_MESSAGE_SOURCE, v: WIDGET_MESSAGE_VERSION, type: 'AUTH', payload };
    }
    case 'RESIZE': {
      const payload = parseResizePayload(data.payload);
      if (payload === null) {
        return null;
      }
      return { source: WIDGET_MESSAGE_SOURCE, v: WIDGET_MESSAGE_VERSION, type: 'RESIZE', payload };
    }
    case 'OPEN_VIDEO': {
      const payload = parseOpenVideoPayload(data.payload);
      if (payload === null) {
        return null;
      }
      return {
        source: WIDGET_MESSAGE_SOURCE,
        v: WIDGET_MESSAGE_VERSION,
        type: 'OPEN_VIDEO',
        payload,
      };
    }
    case 'OPEN_LINK': {
      const payload = parseOpenLinkPayload(data.payload);
      if (payload === null) {
        return null;
      }
      return {
        source: WIDGET_MESSAGE_SOURCE,
        v: WIDGET_MESSAGE_VERSION,
        type: 'OPEN_LINK',
        payload,
      };
    }
    default:
      return null;
  }
}

/**
 * Post a `WidgetMessage` to a peer window with an ALWAYS-explicit `targetOrigin`
 * (the pinned peer origin) — NEVER `'*'`. Both sides post through this so no code
 * path can leak a message to an unexpected origin. Fails closed: an empty
 * `targetOrigin` (nothing to pin to) or a missing target window is a no-op rather
 * than a wildcard broadcast.
 */
export function postWidgetMessage(
  target: Window | null | undefined,
  message: WidgetMessage,
  targetOrigin: string,
): void {
  if (!target || targetOrigin.length === 0 || targetOrigin === '*') {
    return;
  }
  target.postMessage(message, targetOrigin);
}

/**
 * Compute an exact origin (`scheme://host[:port]`) from a URL — used host-side to
 * pin the widget's origin from its `widgetUrl`. Reuse this rather than
 * hand-splitting URLs. Returns `''` for a URL the platform cannot parse (which
 * then fails `parseWidgetMessage`/`postWidgetMessage` closed).
 */
export function deriveOrigin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}
